import {useContext, useEffect, useState} from "react";
import Breadcrumb from '../../components/Breadcrumb'
import {getMainWidth, handelTitle} from "../../utils";
import './Wallet.css'
import {toast} from "react-toastify";
import {AppContext} from "../../App.jsx";
import { Link } from '@tanstack/react-router'
import { ipfs2md5 } from "../../utils";
import chainData from "../../chains";
import { useLocation } from '@tanstack/react-router'
import NftDetailModal from "../../components/NftDetailModal";
import { useNavigate } from '@tanstack/react-router'

/**
 * @module containers/Wallet
 * @category Containers
 * @description Wallet information page container component.
 */

/**
 * Format a wallet address for display (truncate middle)
 * @param {string} address - The full Ethereum address
 * @returns {string} Truncated address like "0x1234...5678"
 */
const formatAddress = (address) => {
    if (!address) return '';
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
};

// Chain slug helper — mirrors SecCollectionList's resolveCollectionPath so the
// wallet "View" links match the real /collections/$networkSlug/$collectionSlug
// route (e.g. /collections/anvil/4770).
const toSlug = (value) => `${value || ''}`.trim().toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '');

const chainSlugByNetwork = (() => {
    const map = {};
    for (const entry of chainData || []) {
        if (entry && entry.chainId != null) {
            const raw = entry.chainName || entry.network || entry.chainId;
            map[`${entry.chainId}`] = toSlug(raw);
        }
    }
    return map;
})();

/**
 * Main wallet container component.
 *
 * Displays comprehensive wallet information including:
 * - Wallet summary (NFT count, collections, auctions)
 * - Associated user profile (if wallet is linked to an account)
 * - Owned NFTs grid with pagination
 * - Owned collections
 * - Active auctions
 *
 * Data is provided via loader from the route, eliminating the need
 * for useEffect-based fetching on initial load.
 *
 * @param {Object} props
 * @param {string} props.address - The wallet address
 * @param {Object} props.initialWalletData - Preloaded wallet data from route loader
 * @returns {React.ReactElement} Rendered wallet page
 */
const WalletContainer = ({ address, initialWalletData }) => {

    const _ac = useContext(AppContext)
    const location = useLocation()

    // Resolve the current locale segment (e.g. "en") so wallet links keep the
    // same localized prefix as the rest of the site.
    const currentLocale = (() => {
        const seg = (typeof location.pathname === 'string' ? location.pathname.split('/').filter(Boolean) : []);
        return seg[0] || '';
    })();

    // State for wallet data (from loader) and pagination
    const [walletData, setWalletData] = useState(initialWalletData || null);
    const [currentPage, setCurrentPage] = useState(1);
    const [nftsPage, setNftsPage] = useState(1);
    // NFT detail modal state — mirrors the collection page's activeItem.
    const [activeNft, setActiveNft] = useState<any>(null);
    const navigate = useNavigate();
    // Set of stable img keys whose CDN <img> failed — those render the shimmer
    // placeholder too (a broken <img> icon is worse than the shimmer).
    const [failedImgKeys, setFailedImgKeys] = useState<any>(() => new Set());

    const openNftModal = (nft, resolvedImageUrl) => {
        let meta = null;
        try {
            meta = typeof nft.metadata === 'string' ? JSON.parse(nft.metadata) : (nft.metadata || null);
        } catch (e) { meta = null; }
        setActiveNft({
            item: { meta: meta || {}, coll: nft.collection_id, tokId: nft.token_id },
            imgUrl: resolvedImageUrl || meta?.image || '',
            noMeta: !meta?.attributes || meta.attributes.length === 0,
            chainSlug: chainSlugByNetwork[String(nft.network)] || 'flare',
        });
    };

    // Sync walletData when initialWalletData changes (e.g., navigating to a different wallet)
    useEffect(() => {
        setWalletData(initialWalletData || null);
        // Reset pagination when address changes
        setNftsPage(1);
        setCurrentPage(1);
    }, [initialWalletData]);

    // Initialize title and layout
    useEffect(() => {
        handelTitle('Wallet');
        getMainWidth();
    }, []);

    // If wallet data is not available, render nothing
    if (!walletData) {
        return null;
    }

    const { wallet, user, associated_wallets, owned_nfts, owned_collections, active_auctions, summary } = walletData;

    return (
        <>
            <Breadcrumb text1="Wallet" text2={wallet}/>
        <div className="main-content position-relative bg-gray-100">

            <div className="container-fluid py-4">

                {/* Wallet Header */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="wallet-header">
                            <div className="row align-items-center">
                                <div className="col-md-6">
                                    <h2>
                                        <i className="fas fa-wallet me-2"></i>
                                        Wallet Overview
                                    </h2>
                                    <div className="d-flex align-items-center mb-3">
                                        <code className="wallet-address">
                                            {wallet}
                                        </code>
                                        <button
                                            className="wallet-btn ms-2"
                                            onClick={() => navigator.clipboard.writeText(wallet)}
                                            title="Copy address"
                                        >
                                            <i className="fas fa-copy"></i>
                                        </button>
                                    </div>

                                    {/* Summary Stats */}
                                    <div className="row g-2">
                                        <div className="col-6 col-md-3">
                                            <div className="wallet-stat">
                                                <div className="wallet-stat-value">{summary.total_nfts_owned}</div>
                                                <div className="wallet-stat-label">NFTs Owned</div>
                                            </div>
                                        </div>
                                        <div className="col-6 col-md-3">
                                            <div className="wallet-stat">
                                                <div className="wallet-stat-value">{summary.total_collections_owned}</div>
                                                <div className="wallet-stat-label">Collections</div>
                                            </div>
                                        </div>
                                        <div className="col-6 col-md-3">
                                            <div className="wallet-stat">
                                                <div className="wallet-stat-value">{summary.total_active_auctions}</div>
                                                <div className="wallet-stat-label">Active Auctions</div>
                                            </div>
                                        </div>
                                        <div className="col-6 col-md-3">
                                            <div className="wallet-stat">
                                                <div className="wallet-stat-value">{summary.total_associated_wallets}</div>
                                                <div className="wallet-stat-label">Associated Wallets</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    {user ? (
                                        <div className="wallet-panel">
                                            <h5 className="mb-3">
                                                <i className="fas fa-user-circle me-2"></i>
                                                Associated Account
                                            </h5>
                                            <div className="d-flex align-items-center">
                                                <div className="wallet-avatar me-3">
                                                    <img
                                                        id={`avatar-img-${user.user_id}`}
                                                        src={`https://cdn.stealthisnft.xyz/u/${user.user_id}/profile/avatar.jpg`}
                                                        alt={user.displayname || user.username}
                                                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                                    />
                                                </div>
                                                <div>
                                                    <h6 className="text-light mb-1">
                                                        {user.displayname || user.username}
                                                    </h6>
                                                    <small className="text-light opacity-8">@{user.username}</small>
                                                    {user.role === 'admin' && (
                                                        <span className="wallet-pill ms-2">Admin</span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="mt-3">
                                                <Link
                                                    className="wallet-btn"
                                                    to={`/profile/${user.user_id}`}
                                                >
                                                    <i className="fas fa-external-link-alt me-1"></i>
                                                    View Profile
                                                </Link>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="wallet-panel text-center">
                                            <i className="fas fa-user-slash fa-3x text-light opacity-5 mb-3"></i>
                                            <h5 className="mb-2">Not Associated</h5>
                                            <p className="small mb-0">
                                                This wallet is not linked to any user account.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Associated Wallets Section */}
                {associated_wallets && associated_wallets.length > 0 && (
                    <div className="row mb-4">
                        <div className="col-12">
                            <div className="wallet-card">
                                <h5 className="wallet-section-header mb-3">
                                    <i className="fas fa-link me-2"></i>
                                    Associated Wallets
                                </h5>
                                <div className="row g-2">
                                    {associated_wallets.map((w, idx) => (
                                        <div key={idx} className="col-12 col-md-6 col-lg-4">
                                            <div className="wallet-panel">
                                                <div className="d-flex align-items-center">
                                                    <code className="small"><Link to={"/wallet/"+w.wallet}>{w.wallet}</Link></code>
                                                    {w.wallet === wallet && (
                                                        <span className="wallet-pill ms-2">This Wallet</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Active Auctions Section */}
                {active_auctions && active_auctions.length > 0 && (
                    <div className="row mb-4">
                        <div className="col-12">
                            <div className="wallet-card">
                                <h5 className="wallet-section-header mb-3">
                                    <i className="fas fa-gavel me-2"></i>
                                    Active Auctions ({active_auctions.length})
                                </h5>
                                <div className="table-responsive">
                                    <table className="wallet-table table-hover">
                                        <thead>
                                            <tr>
                                                <th>Auction ID</th>
                                                <th>Item</th>
                                                <th>Current Bid</th>
                                                <th>Ends In</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {active_auctions.map((auction) => (
                                                <tr key={auction.id}>
                                                    <td><code className="small">{auction.id}</code></td>
                                                    <td>
                                                        {auction.itemHash ? (
                                                            <Link to={`/itemdetails/${auction.itemHash}`} className="text-light">
                                                                {auction.title || 'Auction Item'}
                                                            </Link>
                                                        ) : (
                                                            <span>{auction.title || 'Auction Item'}</span>
                                                        )}
                                                    </td>
                                                    <td className="wallet-cell-positive">
                                                        {auction.currentBid ? `${auction.currentBid} FLR` : 'No bids'}
                                                    </td>
                                                    <td>{auction.endTime ? new Date(auction.endTime).toLocaleString() : 'N/A'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Owned Collections Section */}
                {owned_collections && owned_collections.length > 0 && (
                    <div className="row mb-4">
                        <div className="col-12">
                            <div className="wallet-card">
                                <h5 className="wallet-section-header mb-3">
                                    <i className="fas fa-layer-group me-2"></i>
                                    Owned Collections ({owned_collections.length})
                                </h5>
                                <div className="row g-3">
                                    {owned_collections.map((coll) => (
                                        <div key={coll.id} className="col-12 col-md-6 col-lg-4">
                                            <div className="wallet-panel h-100">
                                                <div className="card-body">
                                                    <h6 className="text-light mb-2">
                                                        {coll.name}
                                                        <span className="wallet-pill ms-2">{coll.symbol}</span>
                                                    </h6>
                                                    <small className="d-block mb-2">
                                                        Contract: {formatAddress(coll.contractAddress)}
                                                    </small>
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <small>
                                                            Total Supply: {coll.totalSupply || 'N/A'}
                                                        </small>
                                                        <Link
                                                            className="wallet-btn"
                                                            to={`/${currentLocale}/collections/${chainSlugByNetwork[String(coll.network)] || String(coll.network)}/${coll.id}`}
                                                        >
                                                            <i className="fas fa-eye me-1"></i>
                                                            View
                                                        </Link>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Owned NFTs Section */}
                <div className="row">
                    <div className="col-12">
                        <div className="wallet-card">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="wallet-section-header mb-0">
                                    <i className="fas fa-images me-2"></i>
                                    Owned NFTs
                                </h5>
                                <span className="wallet-pill">
                                    Total: {owned_nfts.total || 0}
                                </span>
                            </div>
                            {owned_nfts.data && owned_nfts.data.length > 0 ? (
                                <>
                                    <div className="row g-3">
                                        {owned_nfts.data.map((nft, idx) => {
                                            // Parse metadata safely
                                            let metadata: any = null;
                                            // Empty by default — when no image resolves we draw the shimmer
                                            // placeholder (like the collection grid) instead of a broken <img>.
                                            let imageUrl = '';
                                            // True once we've resolved a real CDN src to render an <img>.
                                            let hasImage = false;

                                            try {
                                                if (typeof nft.metadata === 'string') {
                                                    metadata = JSON.parse(nft.metadata);
                                                } else {
                                                    metadata = nft.metadata;
                                                }
                                                // Prefer the indexer's imageUrl (ipfs_metadata.imageUrl); fall
                                                // back to metadata.image. Build the canonical CDN url the same
                                                // way the collection loader does: c/nn/md5.jpg, with the raw
                                                // source URL base64-encoded as the query string. When the CDN
                                                // 404s, the backend's 404 handler reads that query, fetches the
                                                // original IPFS/http url, and populates the CDN.
                                                const img = nft.imageUrl || metadata?.image;
                                                if (img) {
                                                    const source = img;
                                                    const cdnUrl = ipfs2md5(source, true) + ".jpg";
                                                    try {
                                                        const fallback = btoa(source);
                                                        imageUrl = `${cdnUrl}?${fallback}`;
                                                    } catch (e) {
                                                        console.warn('Failed to base64 encode image URL for 404 fallback:', e);
                                                        imageUrl = cdnUrl;
                                                    }
                                                    hasImage = true;
                                                }
                                            } catch (e) {
                                                console.warn('Failed to parse NFT metadata:', e);
                                            }

                                            const imgKey = `nft-${nft.collection_id}-${nft.token_id}`;
                                            const imgFailed = failedImgKeys.has(imgKey);
                                            // 404-with-body from the CDN loads as a blank/broken <img> without
                                            // firing onError — so detect it deterministically: once the element
                                            // is complete but decoded to zero pixels, treat it as failed and
                                            // swap to the shimmer placeholder. The img ref callback fires at
                                            // mount (before load finishes), so we re-check shortly after.
                                            const markImgFailedNow = () => {
                                                if (imgFailed) return;
                                                const next = new Set(failedImgKeys);
                                                next.add(imgKey);
                                                setFailedImgKeys(next);
                                            };
                                            const checkImg = (ref) => {
                                                if (!ref) return;
                                                const verify = () => {
                                                    // A true image decodes to non-zero natural size. A CDN 404 often
                                                    // leaves the element stuck at complete=false / size 0 (never
                                                    // flips to complete), so don't gate on `.complete` — zero size
                                                    // after the settle window means it didn't decode.
                                                    if (ref.naturalWidth === 0 && ref.naturalHeight === 0) markImgFailedNow();
                                                };
                                                // Don't check at mount — every img is size 0 before it loads.
                                                // Re-check after the load settles instead.
                                                setTimeout(verify, 1500);
                                                setTimeout(verify, 4000);
                                            };

                                            return (
                                                <div key={idx} className="col-6 col-md-4 col-lg-3 col-xl-2">
                                                    <div className="wallet-panel overflow-hidden h-100" style={{ cursor: 'pointer' }} onClick={() => openNftModal(nft, imageUrl)}>
                                                        {hasImage && !imgFailed ? (
                                                            <img
                                                                id={`nft-img-${nft.collection_id}-${nft.token_id}`}
                                                                src={imageUrl}
                                                                alt={metadata?.name || `${nft.collection_name} #${nft.token_id}`}
                                                                className="nft-image-img"
                                                                style={{
                                                                    height: '200px',
                                                                    width: '100%',
                                                                    objectFit: 'cover'
                                                                }}
                                                                ref={checkImg}
                                                            />
                                                        ) : (
                                                            <div className="pixel-collection-placeholder is-delayed" style={{ minHeight: '200px', aspectRatio: '1 / 1' }} />
                                                        )}
                                                        <div className="card-body p-2">
                                                            <h6 className="text-light mb-1 small">
                                                                {nft.collection_name || 'Unknown Collection'}
                                                            </h6>
                                                            <small className="d-block mb-2">
                                                                #{nft.token_id}
                                                            </small>
                                                            {nft.auctionID && (
                                                                <span className="wallet-pill-warn">
                                                                    <i className="fas fa-gavel me-1"></i>
                                                                    In Auction
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* Pagination */}
                                    {owned_nfts.pagination && owned_nfts.pagination.totalPages > 1 && (
                                        <div className="d-flex justify-content-center mt-4">
                                            <nav>
                                                <ul className="pagination">
                                                    <li className={`page-item ${owned_nfts.pagination.page <= 1 ? 'disabled' : ''}`}>
                                                        <button
                                                            className="page-link wallet-btn"
                                                            onClick={() => setNftsPage(prev => Math.max(1, prev - 1))}
                                                        >
                                                            Previous
                                                        </button>
                                                    </li>
                                                    <li className="page-item disabled">
                                                        <span className="page-link text-light">
                                                            Page {owned_nfts.pagination.page} of {owned_nfts.pagination.totalPages}
                                                        </span>
                                                    </li>
                                                    <li className={`page-item ${owned_nfts.pagination.page >= owned_nfts.pagination.totalPages ? 'disabled' : ''}`}>
                                                        <button
                                                            className="page-link wallet-btn"
                                                            onClick={() => setNftsPage(prev => Math.min(owned_nfts.pagination.totalPages, prev + 1))}
                                                        >
                                                            Next
                                                        </button>
                                                    </li>
                                                </ul>
                                            </nav>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="wallet-empty">
                                    <i className="fas fa-box-open fa-3x opacity-3 mb-3"></i>
                                    <h5 className="mb-2">No NFTs Found</h5>
                                    <p>
                                        This wallet doesn't own any NFTs yet.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <NftDetailModal
          show={!!activeNft}
          onClose={() => setActiveNft(null)}
          title={activeNft?.item?.meta?.name || (activeNft ? `#${activeNft.item.tokId}` : '')}
          item={activeNft?.item}
          imageUrl={activeNft?.imgUrl}
          chainSlug={activeNft?.chainSlug}
          noMeta={activeNft?.noMeta}
          onNavigate={(nav) => navigate(nav)}
        />
    </>
    );
}

export default WalletContainer;